import supabase from '../supabase';
import {
  StaffRecentStudent,
  StaffRecentStudentRow
} from '../../utils/caseNoteTypes';

// ============================================
// RECENT STUDENTS
// ============================================

// Get staff's recent students for quick add
export const getRecentStudents = async (
  staffId: string,
  limit = 5
): Promise<StaffRecentStudent[]> => {
  try {
    const { data, error } = await supabase
      .from('staff_recent_students')
      .select(`
        *,
        students(name, schoolbox_id)
      `)
      .eq('staff_id', staffId)
      .order('last_note_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return (data || []).map((row: StaffRecentStudentRow) => ({
      id: row.id,
      staffId: row.staff_id,
      studentId: row.student_id,
      studentName: row.students?.name || 'Unknown Student',
      schoolboxId: row.students?.schoolbox_id || null,
      lastNoteAt: row.last_note_at,
      noteCount: row.note_count
    }));
  } catch (error) {
    console.error('Error fetching recent students:', error);
    throw error;
  }
};

// Update recent student tracker (called when creating a case note)
export const updateRecentStudent = async (
  staffId: string,
  studentId: string
): Promise<void> => {
  try {
    // Try to update existing record
    const { data: existing } = await supabase
      .from('staff_recent_students')
      .select('id, note_count')
      .eq('staff_id', staffId)
      .eq('student_id', studentId)
      .single();

    if (existing) {
      // Update existing record
      await supabase
        .from('staff_recent_students')
        .update({
          last_note_at: new Date().toISOString(),
          note_count: existing.note_count + 1
        })
        .eq('id', existing.id);
    } else {
      // Insert new record
      await supabase
        .from('staff_recent_students')
        .insert([{
          staff_id: staffId,
          student_id: studentId,
          last_note_at: new Date().toISOString(),
          note_count: 1
        }]);
    }
  } catch (error) {
    // Non-critical error, just log it
    console.error('Error updating recent student:', error);
  }
};

// ============================================
// STUDENT SEARCH
// ============================================

// Search students by name
export const searchStudents = async (
  query: string,
  limit = 10,
  signal?: AbortSignal
): Promise<{ id: string; name: string; studentId: string; yearLevel: string; schoolboxId: string | null }[]> => {
  try {
    let queryBuilder = supabase
      .from('students')
      .select('id, name, student_id, year_level, schoolbox_id')
      .ilike('name', `%${query}%`)
      .order('name', { ascending: true })
      .limit(limit);

    if (signal) {
      queryBuilder = queryBuilder.abortSignal(signal);
    }

    const { data, error } = await queryBuilder;

    if (error) throw error;

    return (data || []).map(s => ({
      id: s.id,
      name: s.name,
      studentId: s.student_id,
      yearLevel: s.year_level || '',
      schoolboxId: s.schoolbox_id || null
    }));
  } catch (error) {
    console.error('Error searching students:', error);
    throw error;
  }
};

// Get all subjects for dropdown
export const getSubjects = async (): Promise<{ id: string; name: string; code: string }[]> => {
  try {
    const { data, error } = await supabase
      .from('subjects')
      .select('id, name, code')
      .order('name', { ascending: true });

    if (error) throw error;

    return (data || []).map(s => ({
      id: s.id,
      name: s.name,
      code: s.code
    }));
  } catch (error) {
    console.error('Error fetching subjects:', error);
    throw error;
  }
};
